import { StylishNewSearchBar } from '../DesignSystems/New/StylishNewSearchBar';
import SitrepListItem from './SitrepListItem';
import React, { useCallback, useMemo, useState } from 'react';
import SitrepCreateDrawer from './SitrepCreateDrawer';
import generateUUID from '../../utils/sharedUtils/generateUUID';
import sitrepName from './DisasterGPTDataHelpers/sitrepName';
import { toast } from 'react-toastify';
import {
  useInitSitrepV2,
  useSitrepsV2,
  useUpdateSitrepV2,
} from './hooks/useSitrepsV2';
import { useAors } from '../../hooks/useAors';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import PageTitle from '../headers/PageTitle';
import Loader from '../Loader/Loader';
import { FirstSitrepPage } from './FirstSitrepPage';
import { GetSitrepsResponse } from 'components/Reports/reports.types';
import { useAorText } from 'utils/aorTerminology';

interface SitrepTitleRowProps {
  selectedAorId?: UUID | null;
  setSelectedAorId: (selectedAorId: UUID | null) => void;
  addSitrep: () => void;
  filterText: string;
  setFilterText: (filterText: string) => void;
  hideOptions?: boolean;
}
const SitrepTitleRow = ({
  setSelectedAorId,
  addSitrep,
  filterText,
  setFilterText,
  hideOptions = false,
}: SitrepTitleRowProps) => {
  const aorText = useAorText();
  const { data: aors = [] } = useAors();

  const options = useMemo(
    () =>
      aors.map((aor) => ({
        value: aor.id,
        label: aor.name,
      })),
    [aors]
  );

  const handleSelect = (
    selectedOption: { value: UUID; label: string } | null
  ) => setSelectedAorId(selectedOption?.value ?? null);

  return (
    <PageTitle title="PRATUS AI SITREPS">
      <div className="d-flex flex-row gap-3">
        {!hideOptions && (
          <>
            <div className="flex-grow-1">
              <StylishNewSearchBar
                value={filterText}
                onChangeSearchTerm={(val) => setFilterText(val)}
                placeholder="Search PRATUS AI SITREPs by name..."
              />
            </div>
            <StylishNewSelect
              title={aorText('Filter by AOR')}
              options={options}
              isClearable
              onChange={handleSelect}
              placeholder={aorText('Filter by AOR')}
            />
            <StylishNewButton className="button--primary" onClick={addSitrep}>
              Create PRATUS AI SITREP
            </StylishNewButton>
          </>
        )}
      </div>
    </PageTitle>
  );
};

type SitrepManagerProps = {
  handleSitrepSelect: (sitrep: UUID) => void;
};

const SitrepManager: React.FC<SitrepManagerProps> = ({
  handleSitrepSelect,
}) => {
  const { data: sitreps = [], isLoading, isError } = useSitrepsV2();
  const initSitrepMutation = useInitSitrepV2();
  const updateSitrepMutation = useUpdateSitrepV2();
  const [filterText, setFilterText] = useState('');
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editingSitrep, setEditingSitrep] = useState<GetSitrepsResponse | null>(
    null
  );
  const [selectedAorId, setSelectedAorId] = useState<UUID | null>(null);

  const handleCreateSitrep = async (formData: any) => {
    try {
      const sitrepIdResult = await initSitrepMutation.mutateAsync({
        sitrep_template_id: formData.sitrep_template_id || null,
        sitrep_source: 'MANUAL',
        sitrep_source_id: generateUUID(),
        name: formData.name || sitrepName('MANUAL'),
        description: formData.description || '',
        selectedDatetime: new Date().toISOString(),
        aor_id: formData.aor_id || null,
        incident_id: formData.incident_id || null,
      });
      handleSitrepSelect(sitrepIdResult);
      setShowCreateDrawer(false);
      setEditingSitrep(null);
    } catch (e: any) {
      toast.error('Error creating PRATUS AI SITREP: ' + e.message);
    }
  };

  const handleUpdateSitrep = async (formData: any) => {
    if (!editingSitrep) return;

    try {
      await updateSitrepMutation.mutateAsync({
        sitrep_id: editingSitrep.id,
        name: formData.name,
        description: formData.description,
        aor_id: formData.aor_id,
        incident_id: formData.incident_id,
      });
      setEditingSitrep(null);
      setShowCreateDrawer(false);
    } catch (e: any) {
      toast.error('Error updating PRATUS AI SITREP: ' + e.message);
    }
  };

  const handleSaveSitrep = (formData: any) => {
    if (editingSitrep) {
      handleUpdateSitrep(formData);
    } else {
      handleCreateSitrep(formData);
    }
  };

  const handleSitrepClick = useCallback(
    (sitrep: GetSitrepsResponse) => {
      handleSitrepSelect(sitrep.id);
    },
    [handleSitrepSelect]
  );

  const handleAddSitrep = () => {
    setEditingSitrep(null);
    setShowCreateDrawer(true);
  };

  const filteredSitreps = useMemo(() => {
    const lowerCaseFilter = filterText.toLowerCase();
    let currentlyFilteredSitreps = sitreps;
    if (lowerCaseFilter) {
      currentlyFilteredSitreps = currentlyFilteredSitreps.filter((sitrep) => {
        if (typeof sitrep.name === 'string') {
          return sitrep.name.toLowerCase().includes(lowerCaseFilter);
        } else {
          console.warn('Encountered sitrep with invalid name:', sitrep);
          return false;
        }
      });
    }
    if (selectedAorId) {
      currentlyFilteredSitreps = currentlyFilteredSitreps.filter(
        (sitrep) => sitrep.aor_id === selectedAorId
      );
    }
    return currentlyFilteredSitreps;
  }, [filterText, sitreps, selectedAorId]);

  const determineBodyContent = useCallback(() => {
    if (isLoading) {
      return <Loader />;
    }
    if (sitreps.length === 0) {
      return (
        <div className="empty-sitrep-container">
          <FirstSitrepPage onClick={handleAddSitrep} />
        </div>
      );
    }
    if (filteredSitreps.length === 0) {
      return (
        <div className="empty-sitrep-container">
          <h3>No results! Try limiting your search inputs.</h3>
        </div>
      );
    }
    return (
      <div className="sitrep-list">
        {filteredSitreps.map((sitrep) => (
          <SitrepListItem
            key={sitrep.id}
            sitrep={sitrep}
            onClick={() => handleSitrepClick(sitrep)}
          />
        ))}
      </div>
    );
  }, [filteredSitreps, handleSitrepClick, isLoading, sitreps.length]);

  if (isError) {
    return (
      <div className="mt-4 text-center">
        <span>Error loading sitreps.</span>
      </div>
    );
  }

  return (
    <div className="sitrep-page-container">
      <SitrepTitleRow
        addSitrep={handleAddSitrep}
        selectedAorId={selectedAorId}
        setSelectedAorId={setSelectedAorId}
        filterText={filterText}
        setFilterText={setFilterText}
        hideOptions={sitreps.length === 0}
      />
      {determineBodyContent()}
      {showCreateDrawer && (
        <SitrepCreateDrawer
          onHide={() => {
            setShowCreateDrawer(false);
            setEditingSitrep(null);
          }}
          onSave={handleSaveSitrep}
          sitrep={editingSitrep}
        />
      )}
    </div>
  );
};

export default SitrepManager;
